import { SimpleSearchFilter } from 'module/api/filter/types';
import { User } from 'module/api/user/types';
import { GroupedFilter } from 'module/types/filter-field/grouped-filter/filter';
import {
  useQuery,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
  queryOptions,
  useInfiniteQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { EmailAPI, EmailSettings, PaginatedEmailsResponse } from 'api/email';

/** Excludes keys that would break the optimistic update pattern if overridden. */
type SafeEmailSettingsMutationOptions = Omit<
  UseMutationOptions<
    EmailSettings,
    unknown,
    EmailSettings,
    { fallback: EmailSettings | undefined }
  >,
  'mutationFn' | 'onMutate' | 'onSuccess' | 'onError'
>;

const SELECTED_EMAIL_KEY = ['selectedEmail'] as const;
const ALL_EMAILS_WITH_FILTERS_KEY = ['emails', 'filtered'] as const;

export const EMAIL_SETTINGS_KEY = ['email-settings'];

export interface BlacklistedDomain {
  id: string;
  email: string;
  updatedBy: User;
  updatedOn: string;
}

export const useGetEmailSettings = () => {
  return useQuery<EmailSettings>({
    queryKey: EMAIL_SETTINGS_KEY,
    queryFn: async () => {
      return EmailAPI.getEmailSettings();
    },
  });
};

export const useUpdateEmailSettings = (
  mutationOptions: SafeEmailSettingsMutationOptions = {},
) => {
  const queryClient = useQueryClient();

  return useMutation<
    EmailSettings,
    unknown,
    EmailSettings,
    { fallback: EmailSettings | undefined }
  >({
    ...mutationOptions,
    mutationFn: async (settings: EmailSettings) => {
      return EmailAPI.updateEmailSettings(settings);
    },
    onMutate: async (newSettings) => {
      await queryClient.cancelQueries({ queryKey: EMAIL_SETTINGS_KEY });

      const previousData: EmailSettings | undefined =
        queryClient.getQueryData(EMAIL_SETTINGS_KEY);

      queryClient.setQueryData(EMAIL_SETTINGS_KEY, newSettings);

      return { fallback: previousData };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EMAIL_SETTINGS_KEY });
    },
    onError: (_err, _newData, context) => {
      if (context?.fallback) {
        queryClient.setQueryData(EMAIL_SETTINGS_KEY, context.fallback);
      }
    },
  });
};

export const useCreateEmailSettings = (
  mutationOptions: SafeEmailSettingsMutationOptions = {},
) => {
  const queryClient = useQueryClient();

  return useMutation<
    EmailSettings,
    unknown,
    EmailSettings,
    { fallback: EmailSettings | undefined }
  >({
    ...mutationOptions,
    mutationFn: async (settings: EmailSettings) => {
      return EmailAPI.createEmailSettings(settings);
    },
    onMutate: async (newSettings) => {
      await queryClient.cancelQueries({ queryKey: EMAIL_SETTINGS_KEY });

      const previousData: EmailSettings | undefined =
        queryClient.getQueryData(EMAIL_SETTINGS_KEY);

      queryClient.setQueryData(EMAIL_SETTINGS_KEY, newSettings);

      return { fallback: previousData };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EMAIL_SETTINGS_KEY });
    },
    onError: (_err, _newData, context) => {
      if (context?.fallback) {
        queryClient.setQueryData(EMAIL_SETTINGS_KEY, context.fallback);
      }
    },
  });
};

export const useGetBlacklistedDomains = () => {
  return useQuery<BlacklistedDomain[]>({
    queryKey: ['blacklisted-domains'],
    queryFn: async () => {
      return EmailAPI.getBlacklistedDomains();
    },
  });
};

export const useCreateBlacklistedDomain = (): UseMutationResult<
  any,
  unknown,
  { email: string }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ email }) => EmailAPI.createBlacklistedDomain(email),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blacklisted-domains'] });
    },
  });
};

export const useUpdateBlacklistedDomain = (): UseMutationResult<
  any,
  unknown,
  { oldEmail: string; newEmail: string }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ oldEmail, newEmail }) =>
      EmailAPI.updateBlacklistedDomain({ oldEmail, newEmail }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blacklisted-domains'] });
    },
  });
};

export const useDeleteBlacklistedDomain = (): UseMutationResult<
  any,
  unknown,
  { email: string }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ email }) => EmailAPI.deleteBlacklistedDomain(email),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blacklisted-domains'] });
    },
  });
};

export const useSelectedEmailId = () => {
  const queryClient = useQueryClient();

  return {
    selectedEmailId: (
      queryClient.getQueryData(SELECTED_EMAIL_KEY) as
        | { emailId: number; emailSubject: string }
        | undefined
    )?.emailId,
    selectedEmailSubject: (
      queryClient.getQueryData(SELECTED_EMAIL_KEY) as
        | { emailId: number; emailSubject: string }
        | undefined
    )?.emailSubject,
    setSelectedEmailId: (emailId: number, emailSubject: string) => {
      queryClient.setQueryData(SELECTED_EMAIL_KEY, {
        emailId,
        emailSubject,
      });
    },
  };
};

export const useGetAllEmailsQueryBasedOnFilterPaginated = (
  filters: GroupedFilter | undefined,
  params: {
    enabled: boolean;
    limit?: number;
    companyId?: number;
  },
) => {
  return useInfiniteQuery({
    queryKey: [
      ...ALL_EMAILS_WITH_FILTERS_KEY,
      filters,
      params.companyId,
      'paginated',
    ] as const,
    queryFn: ({ pageParam = 0 }) =>
      EmailAPI.getAllEmailsWithFilters(filters, {
        offset: pageParam,
        limit: params.limit || 25,
        companyId: params.companyId,
      }),
    enabled: params.enabled,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const currentOffset = (allPages.length - 1) * (params.limit || 25);
      const hasMore = lastPage.emailThreads.length === (params.limit || 25);

      return hasMore ? currentOffset + (params.limit || 25) : undefined;
    },
    ...queryOptions,
  });
};

export const useGetAllEmailsQueryWithScrollPagination = (
  filters: GroupedFilter | undefined,
  params: {
    enabled: boolean;
    limit?: number;
    companyId?: number;
  },
) => {
  const infiniteQuery = useGetAllEmailsQueryBasedOnFilterPaginated(
    filters,
    params,
  );

  const emails =
    infiniteQuery.data?.pages?.flatMap((page) => page.emailThreads) ?? [];
  const totalCount = infiniteQuery.data?.pages?.[0]?.count ?? 0;

  return {
    emails,
    totalCount,
    isLoading: infiniteQuery.isLoading,
    isFetchingNextPage: infiniteQuery.isFetchingNextPage,
    hasNextPage: infiniteQuery.hasNextPage,
    fetchNextPage: infiniteQuery.fetchNextPage,
    error: infiniteQuery.error,
    refetch: infiniteQuery.refetch,
  };
};

const ALL_EMAILS_WITH_SIMPLE_FILTER_KEY = ['emails', 'simple-filter'] as const;

export const useSearchEmailsWithSimpleFilterPaginated = (
  filter: SimpleSearchFilter | undefined,
  params: {
    enabled: boolean;
    limit?: number;
    initialLimit?: number;
    companyId?: number;
  },
) => {
  const scrollLimit = params.limit ?? 10;
  const firstLimit = params.initialLimit ?? scrollLimit;

  return useInfiniteQuery({
    queryKey: [
      ...ALL_EMAILS_WITH_SIMPLE_FILTER_KEY,
      filter,
      params.companyId,
      'paginated',
    ] as const,
    queryFn: ({ pageParam = 0 }) => {
      const batchSize = (pageParam as number) === 0 ? firstLimit : scrollLimit;

      return EmailAPI.searchEmailsWithSimpleFilter(
        filter as SimpleSearchFilter,
        {
          offset: pageParam as number,
          limit: batchSize,
          companyId: params.companyId,
        },
      );
    },
    enabled: params.enabled && filter != null,
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      const batchSize =
        (lastPageParam as number) === 0 ? firstLimit : scrollLimit;
      const hasMore = lastPage.emailThreads.length === batchSize;

      return hasMore ? (lastPageParam as number) + batchSize : undefined;
    },
    ...queryOptions,
  });
};

export const useSearchEmailsScrollPagination = (
  filter: SimpleSearchFilter | undefined,
  params: {
    enabled: boolean;
    limit?: number;
    initialLimit?: number;
    companyId?: number;
  },
) => {
  const infiniteQuery = useSearchEmailsWithSimpleFilterPaginated(
    filter,
    params,
  );
  const emails =
    infiniteQuery.data?.pages?.flatMap((page) => page.emailThreads) ?? [];
  const totalCount = infiniteQuery.data?.pages?.[0]?.count ?? 0;

  return {
    emails,
    totalCount,
    isLoading: infiniteQuery.isLoading,
    isFetching: infiniteQuery.isFetching,
    isFetchingNextPage: infiniteQuery.isFetchingNextPage,
    hasNextPage: infiniteQuery.hasNextPage,
    fetchNextPage: infiniteQuery.fetchNextPage,
    error: infiniteQuery.error,
    refetch: infiniteQuery.refetch,
  };
};

export const useSearchEmailsByCompanyIdQuery = (
  companyId: number,
  options?: {
    offset?: number;
    limit?: number;
    searchTerm?: string;
  },
  queryOptions?: Omit<
    UseQueryOptions<
      PaginatedEmailsResponse,
      Error,
      PaginatedEmailsResponse,
      (string | number | undefined)[]
    >,
    'queryKey' | 'queryFn'
  >,
): UseQueryResult<PaginatedEmailsResponse, Error> => {
  return useQuery({
    queryKey: [
      'emails',
      'search',
      companyId,
      options?.offset,
      options?.limit,
      options?.searchTerm,
    ],
    queryFn: () => EmailAPI.searchEmailsByCompanyId(companyId, options),
    enabled: !!companyId,
    ...queryOptions,
  });
};
